<template>
    <div class="container">
        <!-- Heading Section -->
        <div class="row">
            <div class="px-2 py-3 text-justify">
                <div class="my-auto mx-2">
                    <h1 class="text-8 text-black mb-4">
                        Simplify Your Financial Planning with Our Loan Calculator
                    </h1>
                    <p class="text-3">
                        Navigating the complexities of loans can be daunting, but with our Loan Calculator, you can gain clarity and make informed decisions. Whether you're considering a personal loan, a car loan, or a home loan, this tool is designed to help you estimate monthly payments, interest, and repayment timelines with ease and accuracy.
                    </p>
                </div>
            </div>
        </div>

        <!-- Reasons to Use Loan Calculator -->
        <div class="row">
            <div class="px-2 py-3 text-justify">
                <div class="my-auto mx-2">
                    <h2 class="text-7 text-black mb-4">
                        3 Reasons To Use Our Loan Calculator
                    </h2>
                    <p class="text-3">
                        Taking out a loan is a significant financial commitment, and understanding its implications is crucial. Here’s how our Loan Calculator makes the process straightforward:
                    </p>
                    <ul class="text-3">
                        <li><strong>Accurate Estimates:</strong> Get precise calculations for monthly payments, total interest, and repayment amounts.</li>
                        <li><strong>Customizable Inputs:</strong> Adjust loan terms, interest rates, and loan amounts to explore different scenarios.</li>
                        <li><strong>User-Friendly Interface:</strong> Input your data and view results instantly, making financial planning easier.</li>
                    </ul>
                </div>
            </div>
        </div>

        <!-- How the Loan Calculator Works -->
        <div class="row">
            <div class="px-2 py-3 text-justify">
                <div class="my-auto mx-2">
                    <h2 class="text-7 text-black mb-4">
                        How the Loan Calculator Works
                    </h2>
                    <p class="text-3">
                        Using the Loan Calculator is simple and intuitive:
                    </p>
                    <ol class="text-3">
                        <li><strong>Enter the Loan Amount:</strong> Specify the total amount you wish to borrow.</li>
                        <li><strong>Set the Interest Rate:</strong> Input the annual interest rate provided by your lender.</li>
                        <li><strong>Choose the Loan Term:</strong> Select the repayment period (e.g., 1 year, 5 years, etc.).</li>
                        <li><strong>Calculate:</strong> Instantly view your estimated monthly payments and other relevant details.</li>
                    </ol>
                </div>
            </div>
        </div>

        <!-- Key Features of the Loan Calculator -->
        <div class="row">
            <div class="px-2 py-3 text-justify">
                <div class="my-auto mx-2">
                    <h3 class="text-6 text-black mb-4">
                        Key Features of Our Loan Calculator
                    </h3>
                    <ul class="text-3">
                        <li><strong>Detailed Breakdown:</strong> See how much of each payment goes toward the principal and interest.</li>
                        <li><strong>Amortization Schedule:</strong> Understand your repayment timeline with a clear breakdown of payments over time.</li>
                        <li><strong>Flexible Scenarios:</strong> Experiment with different loan amounts, terms, and rates to find the best option for your budget.</li>
                    </ul>
                </div>
            </div>
        </div>

        <!-- Who Benefits from the Loan Calculator -->
        <div class="row">
            <div class="px-2 py-3 text-justify">
                <div class="my-auto mx-2">
                    <h3 class="text-6 text-black mb-4">
                        Who Can Benefit from a Loan Calculator?
                    </h3>
                    <ul class="text-3">
                        <li><strong>Car Buyers:</strong> Plan your budget for auto loans.</li>
                        <li><strong>Students:</strong> Explore repayment options for educational loans.</li>
                        <li><strong>Business Owners:</strong> Evaluate financing options for business growth.</li>
                        <li><strong>Homebuyers:</strong> Use our mortgage calculator (insert link to calculator).</li>
                    </ul>
                </div>
            </div>
        </div>

        <!-- Benefits of Using the Loan Calculator -->
        <div class="row">
            <div class="px-2 py-3 text-justify">
                <div class="my-auto mx-2">
                    <h3 class="text-6 text-black mb-4">
                        Benefits of Using Our Loan Calculator
                    </h3>
                    <ul class="text-3">
                        <li><strong>Transparency:</strong> Avoid surprises by knowing exactly what to expect in terms of payments and interest.</li>
                        <li><strong>Financial Planning:</strong> Make informed decisions that align with your budget and financial goals.</li>
                        <li><strong>Time-Saving:</strong> Eliminate the guesswork and calculate loan details in seconds.</li>
                    </ul>
                </div>
            </div>
        </div>

        <!-- How the Loan Calculator Supports Your Goals -->
        <div class="row">
            <div class="px-2 py-3 text-justify">
                <div class="my-auto mx-2">
                    <h3 class="text-6 text-black mb-4">
                        How Our Loan Calculator Supports Your Goals
                    </h3>
                    <p class="text-3">
                        Loans can be life-changing, providing the means to purchase a home, start a business, or achieve other milestones. However, without proper planning, they can also lead to financial strain. Our Loan Calculator empowers you to:
                    </p>
                    <ul class="text-3">
                        <li><strong>Understand Costs:</strong> Know the true cost of borrowing, including total interest over the life of the loan.</li>
                        <li><strong>Compare Options:</strong> Evaluate multiple loans to choose the one that best fits your needs.</li>
                        <li><strong>Plan Repayments:</strong> Ensure you can comfortably meet monthly obligations without overextending your budget.</li>
                    </ul>
                </div>
            </div>
        </div>

        <!-- Real-Life Scenarios for Using the Loan Calculator -->
        <div class="row">
            <div class="px-2 py-3 text-justify">
                <div class="my-auto mx-2">
                    <h3 class="text-6 text-black mb-4">
                        Real-Life Scenarios for Using the Loan Calculator
                    </h3>
                    <ul class="text-3">
                        <li><strong>Home Purchase:</strong> Planning to buy a house? Use the calculator to estimate mortgage payments and determine affordability.</li>
                        <li><strong>Debt Consolidation:</strong> Considering a loan to consolidate existing debts? Calculate potential savings by comparing interest rates.</li>
                        <li><strong>Business Expansion:</strong> Need financing for equipment or operations? Evaluate repayment options to ensure your business stays on track.</li>
                        <li><strong>Educational Goals:</strong> Planning to fund a degree? Explore student loan repayment scenarios to prepare for the future.</li>
                    </ul>
                </div>
            </div>
        </div>

        <!-- Tips for Using the Loan Calculator Effectively -->
        <div class="row">
            <div class="px-2 py-3 text-justify">
                <div class="my-auto mx-2">
                    <h3 class="text-6 text-black mb-4">
                        Tips for Using the Loan Calculator Effectively
                    </h3>
                    <ul class="text-3">
                        <li><strong>Be Realistic:</strong> Enter accurate data to get meaningful results.</li>
                        <li><strong>Consider All Costs:</strong> Include fees, insurance, or other charges when calculating loan expenses.</li>
                        <li><strong>Compare Loans:</strong> Use the calculator to evaluate offers from multiple lenders before making a decision.</li>
                        <li><strong>Planning Ahead:</strong> Use the amortization schedule to anticipate changes in payments over time.</li>
                    </ul>
                </div>
            </div>
        </div>

        <!-- Button to Scroll to Top -->
        <div class="row">
            <div class="px-2 py-3 text-justify">
                <div class="my-auto mx-2">
                    <div class="text-center mt-4">
                        <button
                            @click="scrollToTop"
                            class="btn btn-primary text-4"
                        >
                            Ready to Calculate Your Loan?
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    methods: {
        scrollToTop() {
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        }
    }
}
</script>

<style scoped>
/* Add any specific styles here */
</style>
